import { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Button } from '@artstorefronts/ui';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { useAnalytics } from '../context/analytics';
import { useAuth } from '../context/auth';
import { MARK_MOCKUP_DOWNLOADED } from '../graphql/mutations';
import { useTranslation } from 'react-i18next';

const GENERATE_ALL_MOCKUPS_ZIP = gql`
  mutation GenerateAllMockupsZip($imageId: ID!) {
    generateAllMockupsZip(input: { imageId: $imageId })
  }
`;

const CHECK_MOCKUPS_ZIP_STATUS = gql`
  query CheckMockupsZipStatus($imageId: ID!) {
    mockupsZipStatus(imageId: $imageId) {
      ready
      url
      progress {
        total
        completed
        percentage
      }
    }
  }
`;

interface DownloadAllMockupsProps {
  imageId: number;
  open: boolean;
  className?: string;
}

export default function DownloadAllMockupsButton({ imageId, open, className = '' }: DownloadAllMockupsProps) {
  const { t } = useTranslation('artwork');
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ total: number; completed: number; percentage: number } | null>(null);
  const [generateAllMockupsZip] = useMutation(GENERATE_ALL_MOCKUPS_ZIP, { fetchPolicy: 'no-cache' });
  const { data: zipStatusData, refetch: refetchZipStatus } = useQuery(CHECK_MOCKUPS_ZIP_STATUS, {
    variables: { imageId: imageId.toString() },
    skip: !open || !isGeneratingZip,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const { amplitude, intercom } = useAnalytics();
  const { refetchProfile } = useAuth();
  const [markMockupDownloaded] = useMutation(MARK_MOCKUP_DOWNLOADED);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isGeneratingZip && open) {
      intervalId = setInterval(() => {
        refetchZipStatus();
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isGeneratingZip, open, refetchZipStatus]);

  useEffect(() => {
    if (zipStatusData?.mockupsZipStatus) {
      if (zipStatusData.mockupsZipStatus.progress) {
        setProgress(zipStatusData.mockupsZipStatus.progress);
      }

      if (zipStatusData.mockupsZipStatus.ready && zipStatusData.mockupsZipStatus.url) {
        setZipUrl(zipStatusData.mockupsZipStatus.url);
        setIsGeneratingZip(false);
      }
    }
  }, [zipStatusData]);

  useEffect(() => {
    if (zipUrl) {
      const link = document.createElement('a');
      link.href = zipUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setZipUrl(null);
      setIsGeneratingZip(false);
      amplitude.track('download_all_mockups_completed', {
        image_id: imageId.toString(),
      });
      intercom.trackEvent('download_all_mockups_completed', {
        image_id: imageId.toString(),
      });

      // Mark mockup as downloaded
      markMockupDownloaded({
        variables: { input: { hasMockupDownloads: true } },
      })
        .then(() => refetchProfile())
        .catch((error) => console.error('Error marking mockup as downloaded:', error));
    }
  }, [zipUrl, imageId, amplitude, intercom, markMockupDownloaded, refetchProfile]);

  const handleGenerateAllMockupsZip = () => {
    setIsGeneratingZip(true);
    amplitude.track('download_all_mockups_clicked', {
      image_id: imageId.toString(),
    });
    intercom.trackEvent('download_all_mockups_clicked', {
      image_id: imageId.toString(),
    });
    generateAllMockupsZip({
      variables: { imageId: imageId.toString() },
    }).catch((error) => {
      console.error('Error generating mockups zip:', error);
      setIsGeneratingZip(false);
      // Console-only left the button silently reverting, which reads as a dead click.
      toast.error(t('download-all-mockups.start-failed', "Couldn't start the download. Try again."));
    });
  };

  return (
    <>
      <div className="relative w-full">
        <div className="absolute inset-0 z-0 overflow-hidden rounded-md">
          {isGeneratingZip && progress && (
            <div
              className="h-full bg-gray-900 transition-all ease-linear"
              style={{
                width: progress.percentage >= 75 ? '100%' : `${progress.percentage}%`,
                // Add extra ~25% gap to the progress bar for compressing and uploading time
                // Usually takes ~55 seconds to compress and upload
                transitionDuration: progress.percentage >= 75 ? '55000ms' : '3000ms',
              }}
            />
          )}
        </div>
        <Button
          className={`flex items-center justify-center w-full ${className} relative z-10`}
          onClick={handleGenerateAllMockupsZip}
          disabled={isGeneratingZip}
        >
          <div className="flex items-center justify-center gap-2 w-full">
            <Download className="h-4 w-4" />
            <span>
              {isGeneratingZip && progress
                ? progress?.percentage >= 75
                  ? t('download-all-mockups.compressing')
                  : t('download-all-mockups.preparing', {
                      completed: progress?.completed,
                      total: progress?.total,
                    })
                : t('download-all-mockups.label')}
            </span>
          </div>
        </Button>
      </div>
    </>
  );
}
